import { PlusIcon } from "@xola/icons";
import { Breadcrumb, Button, HeaderToolbar, PopoverList } from "@xola/ui-kit";
import React, { useState } from "react";
import { useIsFetching } from "react-query";
import { useHistory } from "react-router-dom";
import { CouponType, CouponTypeCopy, Permission, SelectionState } from "types";
import { IsGranted } from "../../components/Common/IsGranted";
import { CouponList } from "../../components/Coupon/CouponList";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { Page } from "../Page";
import { useSearchAutoFocus } from "@/hooks/useSearchAutoFocus";
import { CouponTypeFilterSelector } from "@/pages/Marketing/CouponTypeFilterSelector";

interface CreateCouponButtonProps {
    size?: string;
    className?: string;
}

export const CreateCouponButton = ({ size, className }: CreateCouponButtonProps) => {
    const history = useHistory();
    const couponTypes = [CouponType.DISCOUNT, CouponType.VOUCHER, CouponType.BXGY, CouponType.UNLOCK];

    const handleClick = (type: string) => {
        history.push(`/marketing/coupons/create?type=${type}`);
    };

    return (
        <IsGranted permissions={Permission.MODIFY_COUPONS}>
            <PopoverList distance={7} trigger="click">
                <Button color="primary" size={size ?? undefined} icon={<PlusIcon />} className={className ?? undefined}>
                    Create Coupon
                </Button>
                {couponTypes.map((couponType) => (
                    <PopoverList.Item key={couponType} name={couponType} onClickItem={() => handleClick(couponType)}>
                        {CouponTypeCopy[couponType]}
                    </PopoverList.Item>
                ))}
            </PopoverList>
        </IsGranted>
    );
};

export const CouponPage = () => {
    const shouldAutoFocus = useSearchAutoFocus();
    const [search, setSearch] = useState("");
    const [shouldClear, setShouldClear] = useState(false);
    const [couponTypes, setCouponTypes] = useState<SelectionState>({ all: true, selected: [] });
    const isLoading = useIsFetching(["coupons"]);

    const handleReset = () => {
        setCouponTypes({ all: true, selected: [] });
        setSearch("");

        // Clear Search using timeout to clear it externally and avoid Search input text flickering on ESC typing
        setShouldClear(true);
        setTimeout(() => {
            setShouldClear(false);
        }, 1);
    };

    /** Merges {@link CouponCode couponCode}.`uses` property with coupon and returns {@link Coupon} */
    return (
        <Page title="Coupons">
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Marketing</Breadcrumb.Item>
                        <Breadcrumb.Item>Coupons</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>

                    <HeaderToolbar.Search
                        key={shouldClear ? "clear" : "stable"} // to aid clearing the internal state of component when the search is reset externally
                        shouldHideMenu
                        autoFocus={shouldAutoFocus}
                        placeholder="Search by Campaign Name or Code"
                        defaultValue={search}
                        isLoading={isLoading}
                        className="border border-gray-lighter"
                        onChange={setSearch}
                    />
                    <CouponTypeFilterSelector value={couponTypes} onChange={setCouponTypes} />
                    <CreateCouponButton className="w-44" />
                </HeaderToolbar>
                <ColumnLayout.Fill>
                    {(height) => (
                        <CouponList
                            height={height}
                            search={search}
                            types={couponTypes.selected}
                            onResetClick={handleReset}
                        />
                    )}
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
